const INSTAGRAM_API_URL =
  process.env.REACT_APP_INSTAGRAM_API_URL ||
  "https://napi.authkey.io/api/v1/Instagram";

const parseResponse = async (response) => {
  const contentType = response.headers.get("content-type");

  let result;

  if (contentType?.includes("application/json")) {
    result = await response.json();
  } else {
    const text = await response.text();
    result = {
      message: text || "Unexpected API response.",
    };
  }

  if (!response.ok) {
    throw new Error(result?.message || "Instagram API request failed.");
  }

  if (result?.success === false || result?.status === false) {
    throw new Error(result?.message || "Instagram API request failed.");
  }

  return result;
};

export const connectInstagramAccount = async ({ code, userId }) => {
  if (!code) {
    throw new Error("Instagram authorization code is missing.");
  }

  if (!userId) {
    throw new Error("User ID is missing.");
  }

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "ConnectInstagram",
      code,
      user_id: userId,
      // Adding an account should never replace an already-connected one —
      // tell the backend this is an additional connection, not a re-auth
      // of the existing single account.
      mode: "add",
    }),
  });

  return parseResponse(response);
};

// Normalize a connected Instagram account from fetchProfileInfo.
// Backend identifies accounts by insta_user_id (not a generic account_id).
const normalizeConnectedAccount = (account) => ({
  id: String(
    account.insta_user_id ??
    account.instaUserId ??
    account.id ??
    account.account_id ??
    account.ig_account_id ??
    "",
  ),
  instaUserId: String(
    account.insta_user_id ??
    account.instaUserId ??
    account.id ??
    account.account_id ??
    "",
  ),
  username: account.username ?? account.ig_username,
  avatarUrl:
    account.avatar_url ??
    account.avatarUrl ??
    account.profile_picture_url ??
    null,
  followersCount: account.followers_count ?? account.followersCount ?? null,
  status: account.status || (account.token_expired ? "expired" : "connected"),
  connectedAt:
    account.connected_at ?? account.Created ?? account.created_at ?? null,
});

export const fetchInstagramAccountData = async ({ userId, instaUserId }) => {
  if (!userId) {
    throw new Error("User ID is missing.");
  }
  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "getInstagramData",
      user_id: userId,
      // "all" (or omitted) unions every connected account; a specific
      // insta_user_id scopes to just that account.
      insta_user_id: instaUserId || "all",
    }),
  });

  return parseResponse(response);
};

export const fetchProfileInfo = async ({ userId, instaUserId }) => {
  if (!userId) {
    throw new Error("User ID is missing.");
  }

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "fetchProfileInfo",
      user_id: userId,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  const result = await parseResponse(response);
  // fetchProfileInfo returns connected accounts (each with insta_user_id).
  const rawList = Array.isArray(result?.data)
    ? result.data
    : result?.data
      ? [result.data]
      : [];

  return {
    ...result,
    data: rawList.map(normalizeConnectedAccount),
    rawProfiles: rawList,
  };
};

export const fetchInstagramMediaComments = async ({
  userId,
  instaUserId,
  mediaId,
}) => {
  if (!userId) {
    throw new Error("User ID is missing.");
  }

  if (!mediaId) {
    throw new Error("Instagram media ID is missing.");
  }

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "fetchInstagramComments",
      user_id: userId,
      media_id: mediaId,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  return parseResponse(response);
};

export const disconnectInstagramAccount = async ({ userId, instaUserId }) => {
  if (!userId) {
    throw new Error("User ID is missing.");
  }

  if (!instaUserId) {
    throw new Error("Instagram insta_user_id is missing.");
  }

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "disconnectInstagram",
      user_id: userId,
      insta_user_id: instaUserId,
    }),
  });

  return parseResponse(response);
};

export const replyToComment = async ({
  userId,
  instaUserId,
  commentId,
  message,
  mediaId,
  commentText,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!commentId) throw new Error("Instagram comment ID is missing.");
  if (!message) throw new Error("Reply message is missing.");
  if (!mediaId) throw new Error("Instagram media ID is missing.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "replyToComment",
      user_id: userId,
      commentId: commentId,
      media_id: mediaId,
      message,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
      ...(commentText ? { to_comment: commentText } : {}),
    }),
  });

  return parseResponse(response);
};

export const fetchConvo = async ({ userId, instaUserId }) => {
  if (!userId) throw new Error("User ID is missing.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "fetchConvo",
      user_id: userId,
      insta_user_id: instaUserId || "all",
    }),
  });

  return parseResponse(response);
};

export const fetchMessages = async ({
  userId,
  instaUserId,
  conversationId,
  recipientId,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!conversationId) throw new Error("Conversation ID is missing.");
  if (!recipientId) throw new Error("Recipient ID is missing.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "fetchMessages",
      user_id: userId,
      conversationId,
      conversation_id: conversationId,
      recipient_id: recipientId,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  return parseResponse(response);
};

export const replyToMessage = async ({
  userId,
  instaUserId,
  recipientIgsid,
  message,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!recipientIgsid) throw new Error("recipientIgsid is missing.");
  if (!message) throw new Error("Reply message is missing.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "replyToMessage",
      user_id: userId,
      recipientIgsid,
      messageText: message,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  return parseResponse(response);
};

const FILTER_TYPE_BY_LOCATION = {
  comment: "comments",
  conversation: "directMessage",
};

const LOCATION_BY_FILTER_TYPE = {
  comments: "comment",
  directMessage: "conversation",
};

const SENTIMENT_VALUES = ["positive", "neutral", "negative"];

const keywordsToApiString = (keywords) => (keywords || []).join(",");

const apiStringToKeywords = (keyword) =>
  typeof keyword === "string"
    ? keyword
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean)
    : Array.isArray(keyword)
      ? keyword
      : [];

const normalizeSentiments = (value) => {
  const list = apiStringToKeywords(value).map((s) =>
    String(s).toLowerCase().trim(),
  );
  return list.filter((s) => SENTIMENT_VALUES.includes(s));
};

const REPLY_TYPE_TO_API = { url: "link" };
const REPLY_TYPE_FROM_API = { link: "url" };

const blocksToReplySequence = (replyBlocks) =>
  (replyBlocks || []).map((block) => ({
    reply_sequence_type: REPLY_TYPE_TO_API[block.type] || block.type,
    textInput: block.content,
    caption: block.caption || "",
  }));

const replySequenceToBlocks = (replySequence) =>
  (replySequence || []).map((item, index) => ({
    id: item.id || `${item.reply_sequence_type || "block"}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    type: REPLY_TYPE_FROM_API[item.reply_sequence_type] || item.reply_sequence_type,
    content: item.textInput || "",
    caption: item.caption || "",
  }));


const normalizeAutomationRule = (rule) => {
  const rawEnable =
    rule.enable ?? rule.is_active ?? rule.isActive ?? rule.status;

  const isActive =
    rawEnable === undefined || rawEnable === null
      ? true
      : Number(rawEnable) !== 0;

  const rawTrigger =
    rule.trigger_type ??
    rule.triggerType ??
    rule.match_mode ??
    rule.matchMode ??
    "";
  const normalizedTrigger = String(rawTrigger).toLowerCase();

  const keywords = apiStringToKeywords(rule.keyword ?? rule.keywords);
  const sentimentsFromField = normalizeSentiments(
    rule.sentiment ?? rule.sentiments ?? rule.mood,
  );

  // Prefer an explicit trigger_type from the backend. Fall back to
  // detecting sentiment-only keyword payloads so older rows still render.
  let triggerType = "keyword";
  if (
    normalizedTrigger === "sentiment" ||
    normalizedTrigger === "mood" ||
    sentimentsFromField.length > 0
  ) {
    triggerType = "sentiment";
  } else if (
    keywords.length > 0 &&
    keywords.every((k) => SENTIMENT_VALUES.includes(String(k).toLowerCase()))
  ) {
    triggerType = "sentiment";
  }

  const sentiments =
    triggerType === "sentiment"
      ? sentimentsFromField.length > 0
        ? sentimentsFromField
        : keywords
          .map((k) => String(k).toLowerCase())
          .filter((k) => SENTIMENT_VALUES.includes(k))
      : [];

  return {
    id: rule.id ?? rule._id ?? rule.rule_id,
    // Every rule is tied to a single Instagram post/media.
    mediaId:
      rule.media_id ?? rule.mediaId ?? rule.post_id ?? rule.postId ?? null,
    triggerType,
    keywords: triggerType === "keyword" ? keywords : [],
    sentiments,
    matchLocation: LOCATION_BY_FILTER_TYPE[rule.filter_type] || "comment",
    replyBlocks: replySequenceToBlocks(rule.reply_sequence || rule.replyBlocks),
    isActive,
  };
};

export const fetchAutomationRules = async ({
  userId,
  instaUserId,
  token,
  mediaId,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      token,
      method: "fetchInstaAutoReply",
      ...(mediaId ? { media_id: mediaId } : {}),
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  const result = await parseResponse(response);
  const rawList = result?.data || result?.rules || result?.list || [];

  return {
    ...result,
    data: Array.isArray(rawList) ? rawList.map(normalizeAutomationRule) : [],
  };
};

export const setupAutomation = async ({
  userId,
  instaUserId,
  token,
  keywords,
  sentiments,
  triggerType = "keyword",
  matchLocation,
  replyBlocks,
  mediaId,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!mediaId)
    throw new Error(
      "A post must be selected — every automation rule is tied to a specific Instagram post.",
    );

  const isSentiment = triggerType === "sentiment";
  const triggerValues = isSentiment
    ? normalizeSentiments(sentiments)
    : keywords || [];

  if (triggerValues.length === 0) {
    throw new Error(
      isSentiment
        ? "Select at least one sentiment."
        : "At least one keyword is required.",
    );
  }
  if (!replyBlocks || replyBlocks.length === 0)
    throw new Error("At least one reply block is required.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      token,
      method: "instaAutoReply",
      // Keyword field carries either free-text keywords or the selected
      // sentiment labels, depending on trigger_type.
      keyword: keywordsToApiString(triggerValues),
      trigger_type: isSentiment ? "sentiment" : "keyword",
      ...(isSentiment
        ? { sentiment: keywordsToApiString(triggerValues) }
        : {}),
      filter_type: FILTER_TYPE_BY_LOCATION[matchLocation] || "comments",
      reply_sequence: blocksToReplySequence(replyBlocks),
      media_id: mediaId,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  return parseResponse(response);
};

export const updateAutomationRule = async ({
  userId,
  instaUserId,
  token,
  ruleId,
  keywords,
  sentiments,
  triggerType = "keyword",
  matchLocation,
  replyBlocks,
  isActive,
  mediaId,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!ruleId) throw new Error("Rule ID is missing.");
  if (!mediaId)
    throw new Error(
      "A post must be selected — every automation rule is tied to a specific Instagram post.",
    );

  const isSentiment = triggerType === "sentiment";
  const triggerValues = isSentiment
    ? normalizeSentiments(sentiments)
    : keywords || [];

  if (triggerValues.length === 0) {
    throw new Error(
      isSentiment
        ? "Select at least one sentiment."
        : "At least one keyword is required.",
    );
  }
  if (!replyBlocks || replyBlocks.length === 0)
    throw new Error("At least one reply block is required.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      token,
      method: "updateInstaAutoReply",
      id: ruleId,
      _id: ruleId,
      keyword: keywordsToApiString(triggerValues),
      trigger_type: isSentiment ? "sentiment" : "keyword",
      ...(isSentiment
        ? { sentiment: keywordsToApiString(triggerValues) }
        : {}),
      filter_type: FILTER_TYPE_BY_LOCATION[matchLocation] || "comments",
      reply_sequence: blocksToReplySequence(replyBlocks),
      // The backend expects 0/1 here, not true/false.
      ...(isActive === undefined ? {} : { enable: isActive ? 1 : 0 }),
      media_id: mediaId,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  return parseResponse(response);
};



const normalizeReplyReportRow = (row) => {
  const rawType =
    row.filter_type ?? row.reply_type ?? row.replyType ?? row.type;
  const normalizedRawType = String(rawType).toLowerCase();
  const type =
    normalizedRawType.includes("auto") ? "automatic" : "manual";


  const rawReply =
    row.message ?? row.reply_content ?? row.reply_text ?? row.replyText ?? "";
  let replyBlocks = [];
  if (Array.isArray(rawReply)) {
    replyBlocks = rawReply;
  } else if (typeof rawReply === "string" && rawReply.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(rawReply);
      replyBlocks = Array.isArray(parsed) ? parsed : [{ type: "text", content: rawReply }];
    } catch {
      replyBlocks = [{ type: "text", content: rawReply }];
    }
  } else {
    replyBlocks = [{ type: "text", content: rawReply }];
  }

  const replyText = replyBlocks
    .map((block) => block.content || block.textInput || block.caption || "")
    .filter(Boolean)
    .join(" ");

  const rawFrom = row.raw?.from || {};
  const rawMedia = row.raw?.media || {};

  const originalComment =
    row.text ??
    row.comment ??
    row.original_comment ??
    row.originalComment ??
    row.original_message ??
    row.to_comment ??
    row.trigger_text ??
    row.raw?.text ??
    "";

  return {
    id: row.id ?? row._id,
    date:
      row.created_at ?? row.createdAt ?? row.date ?? row.received_at ?? "",
    type,
    channel: row.channel || (row.recipient_id || row.recipientId ? "dm" : "comment"),
    accountId: row.account_id ?? row.accountId ?? row.insta_user_id ?? null,
    accountUsername:
      row.account_username ?? row.accountUsername ?? row.ig_username ?? null,
    recipientName:
      row.commenter_username ||
      row.recipient_username ||
      row.recipientUsername ||
      row.recipient_name ||
      row.from_username ||
      rawFrom.username ||
      "Instagram user",
    mediaId: row.media_id ?? row.mediaId ?? rawMedia.id ?? null,
    mediaType:
      row.media_product_type ?? rawMedia.media_product_type ?? null,
    mediaThumbnail: row.media_thumbnail ?? row.mediaThumbnail ?? null,
    mediaPermalink: row.media_permalink ?? row.mediaPermalink ?? null,
    commentId: row.comment_id ?? row.commentId ?? row.raw?.id ?? null,
    parentCommentId:
      row.parent_comment_id ??
      row.parentCommentId ??
      row.raw?.parent_id ??
      null,
    commenterId: row.commenter_id ?? row.commenterId ?? rawFrom.id ?? null,
    originalComment,
    reply: replyText,
    replyBlocks,
    matchedKeyword: row.matched_keyword ?? row.matchedKeyword ?? null,
    // instaReport itself doesn't send sentiment on most rows. Rather than
    // guessing it locally, InstagramReport.js enriches rows after they load
    // by cross-referencing fetchInstagramComments (which does send real
    // sentiment) via matching commentId — see enrichRowsWithCommentSentiment.
    sentiment: row.sentiment ?? row.sentiment_label ?? row.mood ?? null,
    status: row.sent_status ?? row.status ?? "success",
    stepsSent: row.steps_sent ?? row.stepsSent ?? null,
    commentSource: row.comment_source ?? row.commentSource ?? null,
    ruleId: row.rule_id ?? row.ruleId ?? null,
  };
};


const KEYWORD_LIST_SEPARATOR = /[,|]/;

const collectKnownKeywords = (rows) => {
  const known = new Set();
  rows.forEach((row) => {
    if (!row.matchedKeyword) return;
    String(row.matchedKeyword)
      .split(KEYWORD_LIST_SEPARATOR)
      .map((word) => word.trim().toLowerCase())
      .filter(Boolean)
      .forEach((word) => known.add(word));
  });
  return known;
};

const inferKeywordFromText = (text, knownKeywords) => {
  if (!text) return null;
  const lower = text.toLowerCase();
  for (const keyword of knownKeywords) {
    if (lower.includes(keyword)) return keyword;
  }
  return null;
};


const linkReplyReportRows = (rows) => {
  const matchLogByCommentId = new Map();
  const repliesByParentId = new Map();

  rows.forEach((row) => {
    const isAuditLogRow = Boolean(row.matchedKeyword);
    if (isAuditLogRow && row.commentId && !matchLogByCommentId.has(String(row.commentId))) {
      matchLogByCommentId.set(String(row.commentId), row);
    }
    if (row.parentCommentId) {
      const key = String(row.parentCommentId);
      if (!repliesByParentId.has(key)) repliesByParentId.set(key, []);
      repliesByParentId.get(key).push(row);
    }
  });

  const knownKeywords = collectKnownKeywords(rows);
  const auditLogRowIds = new Set(
    Array.from(matchLogByCommentId.values(), (row) => row.id),
  );
  const mergedRowIds = new Set();

  rows.forEach((row) => {
    if (row.parentCommentId || !row.commentId) return;
    if (auditLogRowIds.has(row.id)) return;
    if (!(row.originalComment || "").trim()) return;

    // Keyword tagging runs regardless of whether a reply was found below
    // — an un-logged comment can still be text-matched against a known
    // keyword even if, for some reason, no reply record exists for it.
    const matchLog = matchLogByCommentId.get(String(row.commentId));
    if (!row.matchedKeyword) {
      row.matchedKeyword = matchLog
        ? matchLog.matchedKeyword
        : inferKeywordFromText(row.originalComment, knownKeywords);
    }
    if (matchLog) mergedRowIds.add(matchLog.id);

    if ((row.reply || "").trim()) return; // already has reply content


    const children = repliesByParentId.get(String(row.commentId)) || [];
    const replyChildren = children.filter((child) => {
      const postedByAccount =
        row.accountId != null && String(child.commenterId) === String(row.accountId);
      const taggedAutoReply = child.commentSource === "auto_reply";
      return (postedByAccount || taggedAutoReply) && (child.originalComment || "").trim();
    });
    if (!replyChildren.length) return;

    row.reply = replyChildren
      .map((child) => child.originalComment)
      .filter(Boolean)
      .join(" / ");
    replyChildren.forEach((child) => mergedRowIds.add(child.id));

    if (matchLog) {
      row.type = matchLog.type;
      row.status = matchLog.status || row.status;
      row.stepsSent = matchLog.stepsSent ?? row.stepsSent;
    } else if (
      replyChildren.some(
        (child) =>
          child.commentSource === "auto_reply" ||
          child.type === "automatic"
      )
    ) {
      row.type = "automatic";
    } else if (row.type === "automatic") {
      // keep the type that normalizeReplyReportRow already set from filter_type
    } else {
      row.type = "manual";
    }
  });

  // return rows.filter((row) => {
  //   if (mergedRowIds.has(row.id)) return false;
  //   const isOrphanedSelfReply =
  //     row.parentCommentId &&
  //     (row.commentSource === "auto_reply" ||
  //       (row.accountId != null && String(row.commenterId) === String(row.accountId)));
  //   if (isOrphanedSelfReply) return false;

  //   return true;
  // });
  
  return rows.filter((row) => {
  // Remove rows that were already merged into their parent comment
  if (mergedRowIds.has(row.id)) return false;

  // Never render child/reply comments as separate top-level comments
  if (row.parentCommentId) return false;

  return true;
});
};


const REPLY_REPORT_FILTER_TYPE = {

  manual: "manual_comments",
  automatic: "auto_comments",
};

export const fetchInstagramReplyReports = async ({
  userId,
  instaUserId,
  token,
  mediaId,
  replyType, // "manual" | "automatic" | undefined ("all")
  dateFrom,
  dateTo,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");


  const isAccountSpecific = Boolean(instaUserId && instaUserId !== "all");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "instaReport",
      user_id: userId,
      token,
      startDate: dateFrom,
      endDate: dateTo,
      filter_type: REPLY_REPORT_FILTER_TYPE[replyType] || "all",
      acc_filter: isAccountSpecific ? "acc_specific" : "all",
      ...(isAccountSpecific ? { insta_user_id: instaUserId } : {}),
    }),
  });

  const result = await parseResponse(response);
  const rawList = result?.data || result?.rules || result?.list || [];
  const allRows = Array.isArray(rawList)
    ? linkReplyReportRows(rawList.map(normalizeReplyReportRow))
    : [];


  const rows = mediaId
    ? allRows.filter((row) => String(row.mediaId) === String(mediaId))
    : allRows;

  return {
    ...result,
    data: rows,
    total: result?.total ?? rows.length,
  };
};

export const deleteAutomationRule = async ({
  userId,
  instaUserId,
  token,
  ruleId,
}) => {
  if (!userId) throw new Error("User ID is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!ruleId) throw new Error("Rule ID is missing.");

  const response = await fetch(INSTAGRAM_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      token,
      method: "deleteInstaAutoReply",
      id: ruleId,
      _id: ruleId,
      ...(instaUserId && instaUserId !== "all"
        ? { insta_user_id: instaUserId }
        : {}),
    }),
  });

  return parseResponse(response);
};